import React from 'react';
import { SEO } from '@/components/SEO';
import { Link } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import Breadcrumbs from '@/components/ui/Breadcrumbs';
import ServiceHero from '@/components/services/ServiceHero';
import ServiceBenefits from '@/components/services/ServiceBenefits';
import ServiceFAQ from '@/components/services/ServiceFAQ';
import ServiceCTA from '@/components/services/ServiceCTA';
import ServiceSchema from '@/components/services/ServiceSchema';
import ServiceCrossLinks from '@/components/services/ServiceCrossLinks';
import LazyImage from '@/components/ui/LazyImage';
import { Droplets } from 'lucide-react';

const PressureWashingPage = () => {
  const serviceData = {
    title: "Professional Pressure Washing in Ventura, CA",
    subtitle: "Transform Your Property with Expert Power Washing Services",
    description: "Remove years of grime, mold, and dirt from your home's exterior with our professional pressure washing services. Our eco-friendly methods restore your property's beauty while protecting Ventura County's environment.",
    icon: <Droplets className="h-16 w-16 text-ventura-blue" />,
    beforeImage: "/lovable-uploads/e57635df-c5ff-463b-a685-e793219f7fff.webp",
    afterImage: "/lovable-uploads/a0c5a82a-7156-4024-be0d-894ad3f62b01.webp"
  };

  const benefits = [
    "Removes stubborn stains and buildup",
    "Increases property value and curb appeal",
    "Prevents damage from mold and mildew",
    "Eco-friendly cleaning solutions",
    "Licensed and insured professionals",
    "Satisfaction guarantee on all work"
  ];

  const faqs = [
    {
      question: "How often should I pressure wash my home in Ventura?",
      answer: "Due to Ventura County's coastal climate, we recommend pressure washing your home annually to prevent salt air damage and maintain your property's appearance."
    },
    {
      question: "Is pressure washing safe for all surfaces?",
      answer: "Our trained professionals use appropriate pressure levels for different surfaces. We adjust techniques for delicate materials like stucco, common in Ventura homes."
    },
    {
      question: "Do you serve all of Ventura County?",
      answer: "Yes! We provide pressure washing services throughout Ventura County, including Camarillo, Fillmore, Moorpark, Oxnard, Port Hueneme, Santa Paula, Simi Valley, Thousand Oaks, and Ventura."
    }
  ];

  return (
    <>
      <SEO
        title="Pressure Washing Ventura CA | Professional Power Washing Services"
        description="Expert pressure washing & eco-friendly soft wash in Ventura, CA. Remove grime, mold & dirt from your property. Serving Ventura County. Free estimates!"
        keywords="pressure washing Ventura CA, power washing Camarillo, exterior cleaning Thousand Oaks, Ventura County pressure washing, emergency pressure washing Ventura County, storm damage cleanup Ventura CA"
        path="/services/pressure-washing"
      />
      <ServiceSchema
        serviceName="Pressure Washing"
        description="Expert pressure washing & eco-friendly soft wash in Ventura, CA. Remove grime, mold & dirt from your property. Serving Ventura County."
        serviceType="Pressure Washing"
        areaServed={["Ventura", "Oxnard", "Camarillo", "Thousand Oaks", "Simi Valley", "Fillmore", "Moorpark", "Port Hueneme", "Santa Paula"]}
        additionalServices={["Soft Wash", "Gutter Cleaning", "Window Cleaning"]}
      />

      <div className="min-h-screen flex flex-col">
        <Navbar />
        <ServiceHero {...serviceData} />
        
        <section className="section-padding bg-white">
          <div className="container mx-auto">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-center mb-8">Why Do Ventura County Homes Need Professional Pressure Washing?</h2>
              <p className="text-lg text-gray-600 mb-8 text-center">
                Ventura County's coastal salt air, marine layer moisture, and inland dust accelerate dirt buildup, mold growth, and surface
                staining faster than in most inland climates. Professional pressure washing removes this buildup before it causes lasting damage,
                protecting your property's value and curb appeal using techniques calibrated to local conditions. After winter storms, we also
                offer emergency pressure washing across Ventura County to clear mud, debris, and mold outbreaks before they set into your siding
                or hardscape.
              </p>
              
              {/* Driveway Pressure Washing Section */}
              <div className="max-w-4xl mx-auto mb-12">
                <h3 className="text-3xl font-bold mb-6 text-center">Driveway Pressure Washing Ventura CA</h3>
                <div className="grid md:grid-cols-2 gap-8 items-center">
                  <div>
                    <p className="text-gray-600 mb-4">
                      Transform your driveway with our professional pressure washing services in Ventura County. Our powerful equipment removes years of built-up oil stains, dirt, and grime that diminish your property's curb appeal. Using hot water pressure washing techniques, we effectively tackle tough stains that cold water simply can't handle.
                    </p>
                    <p className="text-gray-600 mb-4">
                      Our eco-friendly approach protects Ventura's environment while delivering exceptional results. We understand the unique challenges of coastal climate exposure and use specialized techniques for concrete, asphalt, and paver surfaces throughout Ventura County.
                    </p>
                    <div className="bg-primary/10 p-4 rounded-lg mb-4">
                      <h4 className="font-semibold text-primary mb-2">Professional Driveway Cleaning Starting at $200</h4>
                      <p className="text-sm text-gray-600">Includes pre-treatment, pressure washing, and post-cleaning inspection</p>
                    </div>
                    <div className="mb-4">
                      <h4 className="font-semibold mb-2">FAQ: How much does pressure washing cost in Ventura?</h4>
                      <p className="text-gray-600 text-sm">Driveway pressure washing in Ventura typically ranges from $200-$400 depending on size and condition. We provide free estimates for all Ventura County properties.</p>
                    </div>
                    <Link to="/#contact" className="text-primary font-semibold hover:underline">
                      Get Your Free Driveway Cleaning Quote →
                    </Link>
                  </div>
                  <div className="before-after-container">
                    <div className="before-after-image">
                      <LazyImage 
                        src="/lovable-uploads/e57635df-c5ff-463b-a685-e793219f7fff.webp" 
                        alt="Before driveway pressure washing Ventura - dirty stained concrete" 
                        className="w-full h-64 object-cover rounded-lg"
                      />
                      <span className="before-after-label before-label">Before</span>
                    </div>
                    <div className="before-after-image">
                      <LazyImage 
                        src="/lovable-uploads/a0c5a82a-7156-4024-be0d-894ad3f62b01.webp" 
                        alt="After driveway pressure washing Ventura - clean restored concrete" 
                        className="w-full h-64 object-cover rounded-lg"
                      />
                      <span className="before-after-label after-label">After</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-8 items-center mb-12">
                <div>
                  <h3 className="text-2xl font-semibold mb-4">Comprehensive Cleaning Solutions</h3>
                  <p className="text-gray-600 mb-4">
                    Our pressure washing services cover every exterior surface of your Ventura County property:
                  </p>
                  <ul className="list-disc pl-6 text-gray-600 space-y-2">
                    <li>Driveways and walkways</li>
                    <li>Home siding and stucco</li>
                    <li>Decks and patios</li>
                    <li>Fences and gates</li>
                    <li>Commercial buildings</li>
                    <li>Parking lots and garages</li>
                  </ul>
                </div>
                <div className="before-after-container">
                  <div className="before-after-image">
                    <img 
                      src={serviceData.beforeImage} 
                      alt="Dirty driveway before pressure washing in Ventura CA" 
                      className="w-full h-64 object-cover rounded-lg"
                    />
                    <span className="before-after-label before-label">Before</span>
                  </div>
                  <div className="before-after-image">
                    <img 
                      src={serviceData.afterImage} 
                      alt="Clean driveway after professional pressure washing" 
                      className="w-full h-64 object-cover rounded-lg"
                    />
                    <span className="before-after-label after-label">After</span>
                  </div>
                </div>
              </div>

              <div className="mb-12">
                <h3 className="text-2xl font-semibold mb-6">Advanced Pressure Washing Techniques for Ventura County</h3>
                <div className="grid md:grid-cols-2 gap-8">
                  <div>
                    <h4 className="text-xl font-semibold mb-4 text-ventura-blue">Hot Water Pressure Washing</h4>
                    <p className="text-gray-600 mb-4">
                      Our hot water pressure washing systems are incredibly effective for removing oil stains from driveways in Camarillo and Thousand Oaks. The heated water breaks down grease and grime that cold water simply can't handle, making it perfect for commercial properties and heavily soiled residential surfaces.
                    </p>
                  </div>
                  <div>
                    <h4 className="text-xl font-semibold mb-4 text-ventura-blue">Soft Wash Integration</h4>
                    <p className="text-gray-600 mb-4">
                      For delicate surfaces like painted wood siding and stucco common in Ventura County homes, we combine low-pressure techniques with specialized cleaning solutions. This approach safely removes organic growth while preserving your property's beautiful exterior finishes.
                    </p>
                  </div>
                </div>
              </div>

              <div className="mb-12">
                <h3 className="text-2xl font-semibold mb-6">Local Expertise for Ventura County Conditions</h3>
                <p className="text-gray-600 mb-4">
                  Understanding Ventura County's diverse microclimates is crucial for effective pressure washing. From the salt air corrosion near Port Hueneme and Oxnard to the dust accumulation in inland areas like Fillmore and Santa Paula, each location presents unique challenges that require specialized approaches.
                </p>
                <p className="text-gray-600 mb-4">
                  Our team's deep knowledge of local conditions means we know exactly how to address the specific contamination patterns affecting your property. Whether it's removing algae buildup common in Simi Valley's shaded areas or tackling the tough dirt accumulation typical in Moorpark's agricultural surroundings, we have the experience and equipment to deliver exceptional results.
                </p>
                <p className="text-gray-600 mb-6">
                  We also understand the importance of water conservation in California. Our efficient pressure washing systems use precisely the right amount of water pressure and volume for each surface type, ensuring effective cleaning while being mindful of our environmental responsibilities.
                </p>
              </div>

              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-2xl font-semibold mb-4">Eco-Friendly Pressure Washing Solutions</h3>
                <p className="text-gray-600 mb-4">
                  As a locally-owned Ventura County business, we're committed to protecting our beautiful environment while delivering superior cleaning results. Our eco-friendly pressure washing approach uses biodegradable cleaning solutions that are safe for your family, pets, and the surrounding landscape.
                </p>
                <p className="text-gray-600">
                  All our cleaning products are carefully selected to be effective against dirt, mold, and mildew while remaining harmless to Ventura County's native plants and wildlife. We properly contain and dispose of all wastewater according to local environmental regulations, ensuring our cleaning process protects the watershed that makes our region so special.
                </p>
              </div>
            </div>
          </div>
        </section>

        <ServiceBenefits benefits={benefits} />
        <ServiceCrossLinks 
          currentService="Pressure Washing"
          relatedServices={[
            { name: 'Soft Wash', path: '/services/soft-wash', icon: 'soft' },
            { name: 'Gutter Cleaning', path: '/services/gutter-cleaning', icon: 'gutter' },
            { name: 'Window Cleaning', path: '/services/window-washing', icon: 'window' },
          ]}
        />
        <ServiceFAQ faqs={faqs} serviceName="Pressure Washing" />
        <ServiceCTA 
          title="Ready for a Spotless Property?"
          description="Get your free pressure washing estimate today and see the difference professional cleaning makes."
        />
        <Footer />
      </div>
    </>
  );
};

export default PressureWashingPage;